import React from 'react';
import { ChevronLeft, ChevronRight } from 'react-feather';
import { ToolbarButton } from '../ConfigPages';

/**
 * WizardFrame — the framed body and the ONE footer every wizard step shares.
 *
 * Previously each step component rendered its own Back/Continue pair, so the
 * six schema steps had six different footers (different labels, different
 * alignment, different disabled rules, one of them missing Back entirely).
 * Steps now render only their content; navigation is the frame's job.
 *
 * props
 *   title, subtitle      step heading
 *   step, ofSteps        position in the flow — rendered as "Step 3 of 6" so
 *                        the answer to "where am I" is on the step itself, not
 *                        only in the rail
 *   headActions          node — step-scoped actions (rare)
 *   flush                bool — body owns its own padding (tables, editors)
 *   back                 { label?, onClick, disabled? }   omit to hide
 *   next                 { label?, onClick, disabled?, busy?, variant? }
 *   secondary            node — extra footer buttons left of `next` (e.g. Skip)
 *   note                 string|node — footer hint, e.g. why Continue is off
 *   noteTone             'warn' to tint the note
 *   children             the step body
 */
export default function WizardFrame({
  title,
  subtitle,
  step,
  ofSteps,
  headActions,
  flush = false,
  back,
  next,
  secondary,
  note,
  noteTone,
  children,
}) {
  return (
    <section className="ep-body">
      {(title || headActions) ? (
        <header className="ep-body-head">
          <div className="ep-body-head-text">
            {step && ofSteps ? <div className="ep-body-head-step">Step {step} of {ofSteps}</div> : null}
            {title ? <h2>{title}</h2> : null}
            {subtitle ? <p>{subtitle}</p> : null}
          </div>
          {headActions ? <div className="ep-body-head-actions">{headActions}</div> : null}
        </header>
      ) : null}

      <div className={`ep-body-inner${flush ? ' ep-flush' : ''}`}>{children}</div>

      {(back || next || secondary || note) ? (
        <footer className="ep-foot">
          {back ? (
            <ToolbarButton icon={ChevronLeft} onClick={back.onClick} disabled={back.disabled}>
              {back.label || 'Back'}
            </ToolbarButton>
          ) : null}
          {note ? <div className={`ep-foot-note${noteTone === 'warn' ? ' ep-warn' : ''}`}>{note}</div> : null}
          <div className="ep-foot-spacer" />
          {secondary}
          {next ? (
            <ToolbarButton
              variant={next.variant || 'primary'}
              icon={next.busy ? undefined : ChevronRight}
              onClick={next.onClick}
              disabled={next.disabled || next.busy}
            >
              {next.busy ? (next.busyLabel || 'Working…') : (next.label || 'Continue')}
            </ToolbarButton>
          ) : null}
        </footer>
      ) : null}
    </section>
  );
}
